import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Inbox, MapPin, Clock, DollarSign, ArrowRight } from "lucide-react";

function minsAgo(iso) {
  if (!iso) return null;
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return m;
}

export default function OfficeDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  if (loading) return <AppShell><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div></AppShell>;

  const incoming = projects.filter((p) => ["office_review", "scope_preparing", "estimating"].includes(p.status));
  const priced = projects.filter((p) => ["price_ready", "estimate_presented"].includes(p.status));
  const others = projects.filter((p) => !incoming.includes(p) && !priced.includes(p));

  const Section = ({ title, items, icon: Icon, showTimer }) => (
    <section className="mb-10">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-orange-400" />
        <h2 className="font-display text-2xl font-bold">{title}</h2>
        <span className="text-sm text-slate-500">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="text-slate-500 text-sm bg-slate-900/50 border border-slate-800 rounded-2xl p-6">Nothing here right now.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((p, i) => {
            const mins = minsAgo(p.submitted_at);
            const overdue = showTimer && mins != null && mins > 15;
            return (
              <motion.button
                key={p.id}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                data-testid={`office-project-card-${p.id}`}
                onClick={() => navigate(`/office/${p.id}`)}
                className="text-left rounded-2xl bg-slate-900 border border-slate-800 p-5 hover:border-orange-500/40 transition-colors shadow-xl group"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-display text-xl font-bold">{p.customer_name}</div>
                    <div className="text-sm text-orange-400">Full Bathroom Remodel</div>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
                <div className="text-sm text-slate-500 flex items-center gap-1.5 mb-1"><MapPin className="w-3.5 h-3.5" /> {p.address}</div>
                <div className="text-xs text-slate-600">Salesperson: {p.salesperson_name}</div>
                {showTimer && mins != null && (
                  <div className={`mt-3 flex items-center gap-1.5 text-sm font-mono-data ${overdue ? "text-rose-400" : "text-emerald-400"}`}>
                    <Clock className="w-4 h-4" /> {mins} min in queue {overdue ? "· over target" : "· within 15 min target"}
                  </div>
                )}
                {p.estimate && <div className="mt-2 flex items-center gap-1.5 text-emerald-400 font-bold font-mono-data"><DollarSign className="w-4 h-4" />{p.estimate.amount.toLocaleString()}</div>}
                <div className="mt-3 flex items-center gap-1 text-xs text-slate-500 group-hover:text-orange-400 transition-colors">Open project <ArrowRight className="w-3.5 h-3.5" /></div>
              </motion.button>
            );
          })}
        </div>
      )}
    </section>
  );

  return (
    <AppShell title="Office / Estimating">
      <div className="p-4 sm:p-6 pb-20">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mb-1">Estimating Queue</h1>
        <p className="text-slate-500 mb-8">Turn field submissions into priced proposals — target 15 minutes.</p>
        <Section title="Incoming Submissions" items={incoming} icon={Inbox} showTimer />
        <Section title="Priced / Presented" items={priced} icon={DollarSign} />
        {others.length > 0 && <Section title="All Other Projects" items={others} icon={ArrowRight} />}
      </div>
    </AppShell>
  );
}
